import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Target, TrendingUp, Users, Zap, Star, ArrowRight, Play, Shield, Clock, 
  Brain, Rocket, CheckCircle, MessageSquare, BarChart3, FileText, Calendar,
  DollarSign, Lightbulb, Award, Globe
} from 'lucide-react';

const Features: React.FC = () => {
  const features = [
    {
      icon: Brain,
      title: "AI Idea Validation",
      description: "Get brutally honest feedback from AI investors. No sugar-coating, just insights that matter.",
      benefits: [
        "Critical analysis with probing questions",
        "Market size and profitability ratings",
        "Competition analysis",
        "Revenue model validation"
      ],
      color: "text-blue-600"
    },
    {
      icon: Clock,
      title: "30-Day Sprint Plan",
      description: "Week-by-week breakdown of exactly what to do. No overthinking, just execution.",
      benefits: [
        "Daily task breakdown",
        "Deadline tracking",
        "Progress milestones",
        "Adjustment recommendations"
      ],
      color: "text-green-600"
    },
    {
      icon: TrendingUp,
      title: "ROI Tracker",
      description: "Track every expense and revenue stream. See your progress from spending money to making money.",
      benefits: [
        "Expense logging",
        "Revenue tracking",
        "Profit/loss analysis",
        "Cash flow visualization"
      ],
      color: "text-purple-600"
    },
    {
      icon: FileText,
      title: "Launch Playbooks",
      description: "Proven templates for cold outreach, landing pages, pricing strategies, and launch checklists.",
      benefits: [
        "Cold outreach templates",
        "Landing page copy",
        "Pricing strategies",
        "Launch checklists"
      ],
      color: "text-orange-600"
    },
    {
      icon: Users,
      title: "Community Pods",
      description: "Join small groups of fellow sprinters for accountability, support, and shared experiences.",
      benefits: [
        "3-5 person accountability groups",
        "Real-time chat",
        "Progress sharing",
        "Peer support"
      ],
      color: "text-pink-600"
    },
    {
      icon: Zap,
      title: "Boss Mode (Cashpurr)",
      description: "Your AI boss keeps you accountable with daily tasks, deadline tracking, and motivational nudges.",
      benefits: [
        "Daily task generation",
        "Deadline reminders",
        "Progress tracking",
        "Motivational feedback"
      ],
      color: "text-yellow-600"
    }
  ];

  const stats = [
    { number: "30", label: "Days to Launch" },
    { number: "500+", label: "Entrepreneurs" },
    { number: "₹10L+", label: "Revenue Generated" },
    { number: "85%", label: "Success Rate" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
              Complete Toolkit for
              <br />
              <span className="font-bold">30-Day Success</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed">
              Everything you need to validate, build, and launch your business in 30 days.
              No excuses, just results.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-300 hover:to-orange-400 text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Start Your Streak
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-2 border-white/80 text-black hover:bg-white hover:text-teal-600 text-lg px-12 py-4 font-semibold backdrop-blur-sm">
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-light mb-3">{stat.number}</div>
                <div className="text-gray-600 uppercase tracking-wider text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-light mb-8">Powerful Features</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every tool you need to sprint from idea to revenue
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <feature.icon className={`w-8 h-8 ${feature.color}`} />
                  </div>
                  <CardTitle className="text-2xl font-medium text-black">{feature.title}</CardTitle>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-light mb-8">How It Works</h2>
            <p className="text-xl text-blue-200 max-w-2xl mx-auto">
              Three simple steps to transform your idea into revenue
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-16">
            <div className="text-center group">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-teal-400 to-blue-500 flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform shadow-2xl">
                <Brain className="w-10 h-10 text-white" />
              </div>
              <div className="text-6xl font-thin text-teal-400/30 mb-4">01</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Validate Your Idea</h3>
              <p className="text-blue-200 leading-relaxed">
                Submit your idea through our AI-powered validation system. 
                Get honest feedback and critical questions to answer.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-teal-400 to-blue-500 flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform shadow-2xl">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <div className="text-6xl font-thin text-teal-400/30 mb-4">02</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Follow the Sprint Plan</h3>
              <p className="text-blue-200 leading-relaxed">
                Get your personalized 30-day sprint plan with daily tasks, 
                deadlines, and accountability from Cashpurr.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-teal-400 to-blue-500 flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform shadow-2xl">
                <Rocket className="w-10 h-10 text-white" />
              </div>
              <div className="text-6xl font-thin text-teal-400/30 mb-4">03</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Launch & Get Paid</h3>
              <p className="text-blue-200 leading-relaxed">
                Use proven playbooks and launch strategies to get your 
                first paying customer within 30 days.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
              Ready to Start Your Streak?
            </h2>
            <p className="text-xl text-blue-200 mb-12 leading-relaxed">
              Join 500+ entrepreneurs who've already made their first sale
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Start Your Streak Now
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-blue-200">
                <Clock className="w-5 h-5" />
                <span>Free Plan</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
